import { ReactNode } from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, MenuItem, Select as MuiSelect, SelectProps as MuiSelectProps, SxProps, Theme } from '@mui/material';
import { css, styled } from '@mui/material/styles';

import CheckIcon from '~/assets/icons/svgs/check-2.svg?react';
import { Text } from '~/components/ui/text';
import { COLORS } from '~/styles/colors';

interface SelectItem {
  value: string | number;
  label: string | number;
  icon?: () => ReactNode;
}

type CustomSelectProps = MuiSelectProps & {
  items: SelectItem[];
  label?: string;
  labelSx?: SxProps<Theme>;
  AdditionalTopMenuItem?: ReactNode;
  itemBorderBottom?: boolean;
  displayCheckIcon?: boolean;
};

export default function Select({
  items,
  label,
  labelSx = {},
  className,
  AdditionalTopMenuItem,
  placeholder,
  itemBorderBottom = true,
  displayCheckIcon = false,
  ...props
}: CustomSelectProps) {
  const styles = useStyles();

  const select = (
    <StyledSelect
      {...props}
      IconComponent={props.IconComponent || KeyboardArrowDownRoundedIcon}
      MenuProps={{
        sx: {
          '.MuiPaper-root': {
            borderRadius: '10px',
            boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.15)',
            padding: '8px 16px',
          },
          '.MuiList-root': {
            padding: 0,
          },
          ...props.MenuProps?.sx,
        },
        ...props.MenuProps,
      }}
    >
      {placeholder && (
        <StyledMenuItem disabled value="" className="placeholder" $isBorder={itemBorderBottom}>
          {placeholder}
        </StyledMenuItem>
      )}
      {AdditionalTopMenuItem}
      {items.map((item) => (
        <StyledMenuItem key={item.value} value={item.value} $isBorder={itemBorderBottom}>
          {item.icon && item.icon()}
          {displayCheckIcon && (
            <>
              {props.value === item.value ? (
                <Box component="span" sx={styles.checkedCircle} className="check-icon">
                  <CheckIcon width={18} height={17} />
                </Box>
              ) : (
                <Box component="span" sx={styles.circle} />
              )}
            </>
          )}
          {item.label}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );

  if (label) {
    return (
      <StyledContainer className={className}>
        <Text variant="inputSmall" sx={{ marginBottom: '6px', ...labelSx }}>
          {label}
        </Text>
        {select}
      </StyledContainer>
    );
  }

  return select;
}

const StyledSelect = styled(MuiSelect)(() => ({
  borderRadius: '100px',
  outline: 0,
  border: `1px solid ${COLORS.grey200}`,
  height: '40px',
  backgroundColor: COLORS.white,

  '.MuiInputBase-input': {
    padding: '10px 16px',
    fontSize: '15px',
    lineHeight: '18px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '40px',
    boxSizing: 'border-box',

    '.check-icon': {
      display: 'none',
    },
  },
  fieldset: {
    border: 'none',
  },
}));

const StyledContainer = styled('div')(() => ({
  width: '100%',
}));

const StyledMenuItem = styled(MenuItem)<{ $isBorder?: boolean }>(({ $isBorder }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 0',
  lineHeight: '20px',
  '&:not(:last-of-type)': {
    borderBottom: $isBorder ? `1px solid ${COLORS.grey200}` : undefined,
  },
  '&:hover': {
    backgroundColor: `${COLORS.white} !important`,
  },
  '&.Mui-selected': {
    backgroundColor: `${COLORS.white} !important`,
  },
  '&.placeholder': {
    display: 'none !important',
  },
}));

function useStyles() {
  return {
    circle: css`
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 1px solid ${COLORS.grey200};
    `,
    checkedCircle: css`
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: ${COLORS.primary};
      display: flex;
      align-items: center;
      justify-content: center;
    `,
  };
}
